import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { getEnvironmentUrl } from '@helper';
import { Site } from '@model';
import { SiteService, TreeService } from '@service';
import { NodeDefinition } from 'tree/dist/tree';
import { TreeNode } from 'tree/dist/tree/lib/models';

@Component({
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  selector: 'bmd-tree',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrls: ['./bmd-tree.component.scss'],
  templateUrl: './bmd-tree.component.html',
})
export class BMDTreeComponent implements OnDestroy, OnInit {
  loading = true;

  subscribers: Subscription[] = [];
  clicked: TreeNode<Site>;
  expandedNodes: Set<TreeNode<Site>> = new Set<TreeNode<Site>>();
  selectedNodes: Set<TreeNode<Site>> = new Set<TreeNode<Site>>();
  sites: Site[] = [];
  treeData: TreeNode<Site>[] = [];
  selection: TreeNode<Site>[] = [];
  checkbox = false;
  labelIdentifier = 'name';
  iconIdentifier = 'spaceType';
  SITE_SPACE_TYPE = 'Site';
  DEVICE_SPACE_TYPE = 'Device';
  cdnUrl = `${getEnvironmentUrl()}/img/icons`;
  iconDefinitions: Record<string, string> = {
    CALIBRANT: `${this.cdnUrl}/calibrant.svg`,
    FOLDER_OPEN_OUTLINED: `${this.cdnUrl}/folder_open_outlined.svg`,
    PEOPLE_ALT: `${this.cdnUrl}/people_alt.svg`,
    SOLAR: `${this.cdnUrl}/solar.svg`,
  };
  iconMap: Record<string, string> = {
    Device: 'FOLDER_OPEN_OUTLINED',
    Site: 'SOLAR',
  };
  disabledNodeAttribute = 'spaceType';
  disabledNodeValues: string[] = [];
  disabledNodeDefinitions: NodeDefinition = {
    [this.disabledNodeAttribute]: this.disabledNodeValues,
  };

  constructor(
    private router: Router,
    private siteService: SiteService,
    private ngZone: NgZone,
    iconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
    private treeService: TreeService,
  ) {
    Object.keys(this.iconDefinitions).forEach((iconName: string) => {
      iconRegistry.addSvgIcon(iconName, domSanitizer.bypassSecurityTrustResourceUrl(this.iconDefinitions[iconName]));
    });
    this.treeService.selectSiteInList$.subscribe((site: Site) => {
      this.selectedNodes = new Set([site]);
    });
  }

  ngOnInit(): void {
    const sitesSubscriber = this.siteService.getAllSites$().subscribe(sites => {
      this.sites.push(...sites);
      this.treeData = this.sites;
      this.loading = false;
    });

    this.subscribers.push(sitesSubscriber);
  }

  ngOnDestroy(): void {
    this.subscribers.forEach(subscriber => subscriber.unsubscribe());
  }

  onDeviceClicked(device: TreeNode<Site>): void {
    this.ngZone.run(() => {
      this.router.navigate([`/details/${device.id}/view-device`]);
    });
  }

  onSiteClicked(site: TreeNode<Site>): void {
    this.ngZone.run(() => {
      this.router.navigate([`/details/${site.id}/view-site`], { queryParams: { tab: 'configure' } });
    });
  }

  selectionHandler(nodes: TreeNode<Site>[]): void {
    if (!nodes[0]) {
      return;
    }

    if (nodes[0]['spaceType'] === this.SITE_SPACE_TYPE) {
      this.onSiteClicked(nodes[0]);
    }

    if (nodes[0]['siteType'] == this.DEVICE_SPACE_TYPE) {
      this.onDeviceClicked(nodes[0]);
    }
  }
}
